import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Radio, makeStyles } from '@material-ui/core';
import RichTextPreview from '../../../richTextPreview';
import SelectableUserList from '../../../selectableUserList';
import { isArray, isArrayEmpty } from '../../../../../utility/helpers';
import {
  isQuestionnaireQuestionOptionSelectedCheck,
  shouldRenderQuestionnaireQuestionOptionSubject,
} from '../../../../../utility/fillSurveyUtils';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      marginBottom: spacing(2),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    answerContent: {
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      userSelect: 'none',
      '&.selected': {
        backgroundColor: primary.blue6,
        '&$singleSelectDisabled': {
          backgroundColor: primary.bluish8,
        },
      },
    },
    answerWrapper: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'flex-start',
      minWidth: 0,
      padding: spacing(2),
      [breakpoints.up('sm')]: {
        padding: spacing(4),
      },
    },
    answerWrapperGroup: {
      padding: spacing(2, 2, 0, 2),
      [breakpoints.up('sm')]: {
        padding: spacing(4, 4, 0, 4),
      },
    },
    singleSelect: {
      cursor: 'pointer',
    },
    singleSelectDisabled: {
      cursor: 'default',
    },
    radio: {
      pointerEvents: 'none',
      marginRight: spacing(1.5),
      color: primary.bluish4,
      [breakpoints.up('sm')]: {
        marginRight: spacing(4),
      },
    },
    radioChecked: {
      color: `${primary.blue1} !important`,
    },
    radioCheckedHistory: {
      color: `${primary.bluish7} !important`,
    },
    answerText: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: '16px',
      alignSelf: 'center',
      wordWrap: 'break-word',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
  })
);

const MultipleChoice = ({
  subjects = [],
  questionId = 0,
  type,
  option,
  selection = {},
  isDisabled = false,
  isSingleReview = false,
  isHistoryView = false,
  onSelect = () => {},
}) => {
  const classes = useStyles();

  const [subject] = subjects;
  const isSelected = isQuestionnaireQuestionOptionSelectedCheck(
    selection,
    questionId,
    option.id,
    subject
  );

  const isGroupReview =
    !isSingleReview && isArray(subjects) && !isArrayEmpty(subjects);

  const handleSingleSelect = useCallback(() => {
    if (isSingleReview && !isDisabled && !isSelected) {
      onSelect(questionId, type, option.id, subject);
    }
  }, [
    isDisabled,
    isSingleReview,
    isSelected,
    questionId,
    type,
    option.id,
    subject,
    onSelect,
  ]);

  const handleMultipleSelect = useCallback(
    (user, shouldDeleteAnswer) => {
      if (!isDisabled) {
        onSelect(questionId, type, option.id, user, shouldDeleteAnswer);
      }
    },
    [isDisabled, questionId, type, option.id, onSelect]
  );

  const isSubjectSelectedCheck = useCallback(
    user =>
      isQuestionnaireQuestionOptionSelectedCheck(
        selection,
        questionId,
        option.id,
        user
      ),
    [selection, questionId, option.id]
  );

  const isSubjectVisible = useCallback(
    user =>
      shouldRenderQuestionnaireQuestionOptionSubject(
        questionId,
        type,
        option.id,
        selection,
        user
      ),
    [questionId, type, option.id, selection]
  );

  return (
    <div className={classes.root}>
      <div
        className={classNames(classes.answerContent, {
          [classes.singleSelect]: isSingleReview,
          [classes.singleSelectDisabled]: isDisabled,
          selected: isSingleReview && isSelected,
        })}
        onClick={handleSingleSelect}
      >
        <div
          className={classNames(classes.answerWrapper, {
            [classes.answerWrapperGroup]: isGroupReview,
          })}
        >
          {isSingleReview && (
            <Radio
              classes={{
                root: classes.radio,
                checked: classNames(classes.radioChecked, {
                  [classes.radioCheckedHistory]: isHistoryView,
                }),
              }}
              checked={!!isSelected}
              disabled={isDisabled}
              disableRipple
            />
          )}
          <RichTextPreview
            text={option.text}
            className={classes.answerText}
            variant="body1"
          />
        </div>
        {isGroupReview && (
          <SelectableUserList
            questionId={questionId}
            users={subjects}
            selection={selection}
            shouldRenderUser={isSubjectVisible}
            isDisabled={isDisabled}
            isInsightsView={isHistoryView}
            isUserSelectedCheck={isSubjectSelectedCheck}
            onSelect={handleMultipleSelect}
          />
        )}
      </div>
    </div>
  );
};

MultipleChoice.propTypes = {
  option: PropTypes.shape({}).isRequired,
  subjects: PropTypes.arrayOf(PropTypes.shape({})),
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selection: PropTypes.shape({}),
  isSingleReview: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isHistoryView: PropTypes.bool,
  onSelect: PropTypes.func,
};

export default MultipleChoice;
